import { EnvironmentProviders, inject, Provider } from '@angular/core';
import { Router } from '@angular/router';

import {
	createDuplicateRequestApolloLink,
	DUPLICATE_REQUEST_REPORTER,
	DuplicateRequestDetectorService,
} from '@efficiently/client-core-duplicate-request-monitoring';
import { environment } from '@efficiently/client-core-environment';
import {
	APOLLO_CONFIG_TOKEN,
	ApolloConfig,
	createDefaultEndpointResolver,
	provideApollo,
} from '@efficiently/client-core-graphql';
import { ClientLoggerFacadeService, createClientLoggerApolloLink } from '@efficiently/client-core-logger';
import { AppConfigService } from '@efficiently/client-core-services';

import { getIsUnloadTriggered } from './core/services/unload-sync.service';

let cachedPublicKeyBase64: string | undefined;
let cachedPublicKey: Promise<CryptoKey> | undefined;

const base64ToArrayBuffer = (value: string): ArrayBuffer => {
	const binary = atob(value);
	const bytes = new Uint8Array(binary.length);

	for (let index = 0; index < binary.length; index += 1) {
		bytes[index] = binary.charCodeAt(index);
	}

	return bytes.buffer;
};

const arrayBufferToBase64 = (value: ArrayBuffer): string => {
	const bytes = new Uint8Array(value);
	let binary = '';

	bytes.forEach(byte => {
		binary += String.fromCharCode(byte);
	});

	return btoa(binary);
};

const getEmailEncryptionKey = (publicKeyBase64: string): Promise<CryptoKey> => {
	if (cachedPublicKeyBase64 !== publicKeyBase64) {
		cachedPublicKeyBase64 = publicKeyBase64;
		cachedPublicKey = globalThis.crypto.subtle.importKey(
			'spki',
			base64ToArrayBuffer(publicKeyBase64),
			{ name: 'RSA-OAEP', hash: 'SHA-256' },
			false,
			['encrypt'],
		);
	}

	return cachedPublicKey as Promise<CryptoKey>;
};

const encryptEmail = async (email?: string): Promise<string | undefined> => {
	const publicKeyBase64 = environment.observability.clientEmailPublicKey;
	if (!email || !publicKeyBase64 || !globalThis.crypto?.subtle) {
		return undefined;
	}

	try {
		const key = await getEmailEncryptionKey(publicKeyBase64);
		const ciphertext = await globalThis.crypto.subtle.encrypt(
			{ name: 'RSA-OAEP' },
			key,
			new TextEncoder().encode(email),
		);

		return arrayBufferToBase64(ciphertext);
	} catch (error) {
		console.error('[Observability] Email encryption failed', error);
		return undefined;
	}
};

export const appGraphqlProviders: Array<Provider | EnvironmentProviders> = [
	{
		provide: APOLLO_CONFIG_TOKEN,
		useFactory: (): ApolloConfig => {
			const appConfig = inject(AppConfigService);
			const loggerFacade = inject(ClientLoggerFacadeService);
			const duplicateRequestDetector = inject(DuplicateRequestDetectorService);
			const duplicateRequestReporter = inject(DUPLICATE_REQUEST_REPORTER);
			const router = inject(Router);

			return {
				getClientEndpoint: createDefaultEndpointResolver(environment.endpoints),
				production: environment.production,
				enableSubscriptions: true,
				enableMutationTracking: true,
				additionalLinks: {
					afterLogging: [createClientLoggerApolloLink(loggerFacade)],
					beforeHttp: [
						createDuplicateRequestApolloLink({
							detector: duplicateRequestDetector,
							reporter: duplicateRequestReporter,
							getRoute: () => router.url,
						}),
					],
				},
				getIsUnloadTriggered,
				getClientHeaders: async (): Promise<Record<string, string>> => {
					const config = appConfig.config;
					const user = config?.user || config?.scopeUser;
					const org = config?.org;
					const headers: Record<string, string> = {};
					const encryptedEmail = await encryptEmail(user?.email);

					const values: Record<string, string | undefined> = {
						'X-Efficiently-Client-User-Id': user?._id,
						'X-Efficiently-Client-User-Email-Encrypted': encryptedEmail,
						'X-Efficiently-Client-Org-Id': org?._id || user?.orgId,
					};

					Object.entries(values).forEach(([key, value]) => {
						if (value) {
							headers[key] = value;
						}
					});

					return headers;
				},
			};
		},
	},
	...provideApollo(),
];
